import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  conceptGroupsApiSlice,
  useGetConceptGroupsQuery,
} from '@verantos-dev/data-access-concept-groups';

import { conceptGroupStatusKeys } from '../api/conceptGroupStatusQueries';

/**
 * OR-6858: a list of ``ResolvedConceptGroupChip``s each own their own
 * ``useGetConceptGroupDetailsQuery`` call, so a table/list with N distinct
 * group ids fires N independent ``GET /concept-groups/{id}`` requests on
 * first render. This hook batches those N lookups into one
 * ``GET /concept-groups?conceptGroupIds=...`` call and seeds each chip's own
 * cache entry from the batch response.
 *
 * Seeding the cache alone is NOT enough: a chip's own query hook fires on
 * MOUNT, in the same render pass as this hook — by the time the batch
 * response arrives and this hook's effect seeds the cache, every chip has
 * already dispatched its own individual request. Cache-seeding after the
 * fact only helps a REMOUNT, not first render, which is the case that
 * actually drives the call volume this fix targets.
 *
 * So this hook returns a defer flag — the caller must thread it to every
 * chip it renders (as ``deferFetch`` on ``ResolvedConceptGroupChip`` /
 * ``defer`` on the chat-card hooks) so those chips skip their own query
 * until the batch call has failed or its response has been written to both
 * caches. The flag flips from the seeding effect's own state update, not from
 * the query's ``isSuccess``: child effects run before this hook's effect, so
 * unblocking on ``isSuccess`` would let chips dispatch before the cache holds
 * the batch result. An id present in the batch response then hits the
 * freshly-seeded cache (no network call);
 * an id absent from it (an unpublished draft — the batch query only hits
 * the published catalog) fires its own individual fetch at that point,
 * which already knows how to fall back to the in-flight draft store.
 *
 * Two UNRELATED caches read concept-group-by-id data, so the seeding effect
 * writes both from the same batch response:
 *  - RTK Query's ``getConceptGroupDetails`` — read by
 *    ``ResolvedConceptGroupChip`` (the artifact-tabs table / fallback).
 *  - TanStack Query's ``['concept-group-status', id]`` — read by
 *    ``useConceptGroupStatusByRef`` (the chat-bubble cards). That query sets
 *    its own non-zero ``staleTime`` precisely so this seeded value isn't
 *    treated as instantly-stale and silently background-refetched the
 *    moment it's unblocked.
 *
 * Gated behind ``concept_group_batch_resolve``; when off, ``uniqueIds.length
 * <= 1``, this always returns ``false`` and chips fetch immediately, exactly
 * as before this hook existed.
 * TODO: cleanup with concept_group_batch_resolve ff
 */
export const useBatchPrewarmConceptGroups = (
  conceptGroupIds: readonly string[],
  /** Optional project scope — forwarded as `projectId`, matching the per-id hook's own scoping. */
  projectId?: string
): boolean => {
  const batchResolveEnabled = Boolean(useFeatureFlagSplit('concept_group_batch_resolve'));
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const uniqueIds = useMemo(
    () => Array.from(new Set(conceptGroupIds.filter((id): id is string => Boolean(id)))),
    [conceptGroupIds]
  );

  // A single id has nothing to batch against — let the chip's own hook handle it.
  const shouldBatch = batchResolveEnabled && uniqueIds.length > 1;

  const urlParams = useMemo(() => {
    const params = uniqueIds.map((id) => `conceptGroupIds=${encodeURIComponent(id)}`);
    if (projectId) params.push(`projectId=${encodeURIComponent(projectId)}`);
    return params.join('&');
  }, [uniqueIds, projectId]);

  // `currentData` (not `data`) so a changed id set never seeds the previous request's response.
  const { currentData, isError } = useGetConceptGroupsQuery({ urlParams }, { skip: !shouldBatch });
  const [seededUrlParams, setSeededUrlParams] = useState<string | null>(null);

  useEffect(() => {
    if (!shouldBatch || !currentData) return;
    (currentData.data ?? []).forEach((group) => {
      if (!group.conceptGroupId) return;
      dispatch(
        conceptGroupsApiSlice.util.upsertQueryData(
          'getConceptGroupDetails',
          { conceptGroupId: group.conceptGroupId },
          { data: group }
        )
      );
      queryClient.setQueryData(conceptGroupStatusKeys.detail(group.conceptGroupId), {
        conceptGroupId: group.conceptGroupId,
        conceptGroupName: group.conceptGroupName,
        assetStatus: group.assetStatus,
      });
    });
    // Child effects run before this one, so chips must stay deferred until this state flips.
    setSeededUrlParams(urlParams);
    // `dispatch`/`queryClient` are stable; only re-run when the batch response
    // actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData, shouldBatch, urlParams]);

  // Not batching → nothing to wait for. Batching → wait until the response has
  // been seeded into both caches (or the call failed) before unblocking chips.
  return shouldBatch && !isError && seededUrlParams !== urlParams;
};
